/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { FC, useState, useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { DropdownMenu } from '@rich-products-corp/primitives-richproducts';
import { NavigationMenu } from '@rich-products-corp/react-ecomm-ui/dist/components/atomic/organisms/NavigationMenu';
import { LineItem } from '@rich-products-corp/react-ecomm-ui/dist/types/cart/LineItem';
import { HeaderMenuProps } from '@rich-products-corp/react-ecomm-ui/dist/types/frontastic';
import { Money } from '@rich-products-corp/react-ecomm-ui/dist/types/product/Money';
import { useQueryClient } from '@tanstack/react-query';
import { setCookie } from 'cookies-next';
import { useSignInDialog } from 'hooks/dialogs/auth/sign-in';
import { fetchApiHub } from 'frontastic';
import { clearAllLocalData, clearSession } from 'frontastic/lib/utils/FrontasticSessionData';
import { useAccount } from 'frontastic/provider';
import { CartFooter } from './cart-footer';
import { CartHeader } from './cart-header';
import { closeOutIcon } from './close-icon';
import { ChevronDownIcon, SearchIcon, ShoppingBagIcon, UserIcon } from './icons';
import { ProductTiles } from './product-tiles';
import { useGetAccount } from '@/hooks/my-account/use-get-account';

export interface HeaderProps extends HeaderMenuProps {
  calloutLink?: string;
  onSignInButtonClick(): void;
}

export const Header: FC<HeaderProps> = (props) => {
  const queryClient = useQueryClient();
  const { data } = useGetAccount();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const cart: any = props.data.useCart;
  const { login, requestConfirmationEmail } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [menuSelected, setMenuSelected] = useState('');

  const signInDialog = useSignInDialog({
    title: 'Sign In to Your Account',
    onSignIn: async (values) => {
      try {
        const response: any = await login(values.username, values.password, undefined, values.turnstileToken);

        fetchApiHub('/action/account/getTokenDetails', { method: 'POST' }, {}).then((res) => {
          if (res.token) {
            setCookie('asio_session', JSON.stringify(res));
          }
        });

        if (response.accountId) {
          queryClient.invalidateQueries({ queryKey: ['get:account'] });
          signInDialog.close();
          return true;
        } else if (response.infoError) {
          await clearSession('frontastic-session');
          await clearAllLocalData();
          queryClient.removeQueries({ queryKey: ['get:account'] });
          return response.message;
        }
      } catch (error) {
        return error;
      }
    },
    requestConfirmationEmail: async (values) => {
      try {
        const response: any = await requestConfirmationEmail(values.username, values.password);

        if (response.success) {
          router.push('/my-account/success-email-verification-pending');
          return true;
        } else if (response.infoError) {
          return response.message;
        }
      } catch (error) {
        return error;
      }
    },
  });

  useEffect(() => {
    const menu = sessionStorage.getItem('menuSelected');
    if (menu) {
      setMenuSelected(menu);
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('bootLogin')) {
      signInDialog.open();
      router.push('/');
    }
  }, [pathname, searchParams]);

  const handleSignOut = async () => {
    await clearSession('frontastic-session');
    await clearAllLocalData();
    await cart?.clearCartAndSession();

    setTimeout(async () => {
      await router.push('/');
      window.location.reload();
    });
  };

  const handleMenuSelected = (menu: string) => {
    sessionStorage.setItem('menuSelected', menu);
    setMenuSelected(menu);
  };

  return (
    <div className="h-[60px] lg:h-[112px]">
      <NavigationMenu
        {...props}
        onMenuSelected={handleMenuSelected}
        userSection={(onClose: () => void) =>
          data?.email ? (
            <>
              <div className="hidden lg:block">
                <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                  <DropdownMenu.Trigger asChild>
                    <button
                      className="group relative -m-2 flex items-center justify-center space-x-2 p-2"
                      onClick={() => signInDialog.open()}
                    >
                      {UserIcon}
                      <span className="ml-2 flex h-full items-center whitespace-nowrap text-center align-middle text-xs font-medium text-gray-700 hover:text-gray-800">
                        Hi, {data.firstName ?? data.email}
                      </span>
                      {ChevronDownIcon}
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content sideOffset={5} className="w-52 rounded-none bg-white p-1 shadow-md">
                    <div className="cursor-pointer">
                      <DropdownMenu.Item onClick={() => router.push('/my-account')}>
                        My Account
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onClick={() => router.push('/my-account/orders')}>
                        Order History
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onClick={() => router.push('/my-account/addresses')}>
                        Address Book
                      </DropdownMenu.Item>
                    </div>
                    <DropdownMenu.Separator className="mx-2 bg-gray-200" />
                    <DropdownMenu.Item onClick={handleSignOut} className="my-2 cursor-pointer">
                      Sign Out
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </div>
              <div className="block lg:hidden">
                <ul>
                  <li className="flex whitespace-nowrap py-3 text-center align-middle text-sm font-medium text-gray-700 hover:text-gray-800">
                    {UserIcon}
                    <span className="my-auto ml-2 flex items-center whitespace-nowrap text-center align-middle text-xs font-medium text-gray-700 hover:text-gray-800">
                      Hi, {data.firstName ?? data.email}
                    </span>
                  </li>
                  <li
                    className="py-2 text-xs font-medium text-gray-700 hover:text-gray-800"
                    onClick={() => {
                      router.push('/my-account');
                      onClose();
                    }}
                  >
                    My Account
                  </li>
                  <li
                    className="py-2 text-xs font-medium text-gray-700 hover:text-gray-800"
                    onClick={() => {
                      router.push('/my-account/orders');
                      onClose();
                    }}
                  >
                    Order History
                  </li>
                  <li
                    className="py-2 text-xs font-medium text-gray-700 hover:text-gray-800"
                    onClick={() => {
                      router.push('my-account/addresses');
                      onClose();
                    }}
                  >
                    Address Book
                  </li>
                  <li
                    className="py-2 text-xs font-medium text-gray-700 hover:text-gray-800"
                    onClick={() => {
                      handleSignOut();
                      onClose();
                    }}
                  >
                    Sign Out
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <button
              className="group relative -m-2 flex items-center justify-center p-2"
              onClick={() => {
                onClose();
                signInDialog.open();
              }}
            >
              {UserIcon}
              <span className="ml-2 flex h-full items-center whitespace-nowrap text-center align-middle text-xs font-medium text-gray-700 hover:text-gray-800">
                Sign In
              </span>
            </button>
          )
        }
        extend={{
          icons: {
            user: UserIcon,
            search: SearchIcon,
            cart: ShoppingBagIcon,
            close: closeOutIcon,
          },
          allowSearch: true,
          cartDrawer: {
            cartHeader: <CartHeader cart={cart} />,
            productTiles: <ProductTiles items={cart.data?.lineItems as LineItem[]} cart={cart} />,
            cartFooter: (
              <CartFooter sum={cart.data?.sum as Money} cart={cart} discountCodes={cart.data?.discountCodes} />
            ),
          },
        }}
      />
    </div>
  );
};
